import EventHub from '@/utils/event';
import i18n from '@/locales';
import { APICode, PROJECT_TYPE, SCENE_ID } from '@/constants';
import { create } from 'zustand';
import { GlobalEvent } from '@/constants/event';
import { toast } from 'react-toastify';
import { TSafariInfo } from '@/types';
import {
  getCredits,
  getSubscriptions,
  getLoginState,
  getPointsActivityPublicKey,
  pointsActivityCheckIn,
  getRemainNums,
} from "@/api";
import {
  loadPaymentScript,
  loadLoginScript,
  encryptWithKey,
  autoShowOpenAppConfirm,
} from "@/utils";

type States = {
  safariInfo: TSafariInfo | null;
  isMobile: boolean;
  adLoaded: boolean ;
  credits: number | undefined;
  userInfo: any;
  isVip: boolean | undefined;
  isSvip: boolean | undefined;
  publicKey: string;
  /** 剩余次数 */
  remainNums: number;
  mutiRemainNums: number;
};

type Getters = {
  getters: {
    isLogin: boolean | undefined;
    creditsVisible: boolean;
  };
};

type Mutations = {
  setCredits: (value: number) => void;
  setUserInfo: (value: any) => void;
  setAdLoaded: (value: boolean) => void;
  setIsMobile: (value: boolean) => void;
  setSafariInfo: (value: TSafariInfo) => void;
};

type Actions = {
  reset: () => void;
  fetchCredits: () => Promise<number>;
  fetchUserInfo: () => Promise<any>;
  fetchVipStatus: () => Promise<void>;
  showPayment: (options: any) => Promise<void>;
  showLogin: (options: any) => Promise<void>;
  receiveCredits: () => Promise<void>;
  fetchSingleRemainNums: () => Promise<void>;
  refreshFreeCount: () => Promise<void>;
};

const initialState: States = {
  safariInfo:null,
  isMobile:false,
  adLoaded: false,
  credits: undefined,
  userInfo: null,
  isVip: undefined,
  isSvip: undefined,
  publicKey: "",
  remainNums: 0,
  mutiRemainNums: 0,
};

const useUserStore = create<States & Getters & Mutations & Actions>(
  (set, get) => ({
    // states
    ...initialState,

    // getters
    getters: {
      get isLogin() {
        const { userInfo } = get();
        if(!userInfo) {
          return  false
        }
        return  userInfo.id?true:false;
      },
      get creditsVisible() {
        const { credits, getters } = get();
        return !!(getters.isLogin && typeof credits === "number");
      },
      get hasRemainNums() {
        const { remainNums } = get();
        return remainNums;
      },
    },

    // mutations
    setSafariInfo: (value) => set({ safariInfo: value }),
    setIsMobile: (value) => set({ isMobile: value }),
    setAdLoaded: (value) => set({ adLoaded: value }),
    setCredits: (value) => set({ credits: value }),
    setUserInfo: (value) => set({ userInfo: value }),

    // actions
    reset: () => set(initialState),
    fetchCredits: async () => {
      const { code, data } = await getCredits();
      if (code === APICode.Success) {
        const { subScore } = data;
        set({ credits: subScore });
        return subScore;
      }
    },
    fetchUserInfo: () => {
      return new Promise<void>((resolve, reject) => {
        getLoginState()
          .then((res: any) => {
            if (res && res.code === "000") {
              set({ userInfo: res.data, remainNums: 0, mutiRemainNums: 0 });
              resolve(res.data);
            } else {
              resolve();
            }
          })
          .catch(reject);
      });
    },
    fetchVipStatus() {
      return new Promise((resolve, reject) => {
        const { userInfo } = get();
        getSubscriptions()
          .then((res: any) => {
            if (res && res.code === "000") {
              let sub = res.data || [];
              if (
                sub.find(
                  (m: any) => m.productName === "fotor" && !m.expiration
                ) ||
                userInfo?.superFotor
              ) {
                set({ isVip: true });
              } else {
                set({ isVip: false });
              }
              if (
                sub.find((m: any) => m.productName === "pxbee" && !m.expiration)
              ) {
                // 超级vip
                set({ isVip: true, isSvip: true });
              } else {
                set({ isSvip: false });
              }
            }
            resolve();
          })
          .catch(reject);
      });
    },
    async showPayment({ position, otherParam }) {
      return new Promise((resolve, reject) => {
        const { fetchVipStatus, fetchCredits, isVip, isSvip } = get();
        loadPaymentScript()
          .then(() => {
            window["Payment"].showPayment(
              async (data: any) => {
                let { type, code } = data;
                if (code === "006" || code === "007") {
                  fetchVipStatus();
                  fetchCredits();
                  resolve();
                } else if (type === "success") {
                  fetchVipStatus();
                  fetchCredits();
                  resolve();
                } else if (type === "fail") {
                  reject();
                } else {
                  reject();
                }
              },
              0,
              isVip || isSvip ? "credit_sub" : "other",
              "",
              "",
              {
                position,
                project_type: PROJECT_TYPE,
              },
              otherParam
            );
          })
          .catch(reject);
      });
    },
    async showLogin({
      isSignIn,
      options,
      isShowScenses,
      otherParam = {},
      callback,
    }) {
      return new Promise<void>((resolve, reject) => {
        loadLoginScript()
          .then(() => {
            let signType = isSignIn ? "login" : "signUp";
            let position = (options && options.position) || "";
            window.login.showLogin(
              signType,
              async (res: any) => {
                let { code, userInfo } = res;

                if (code === 4) {
                  autoShowOpenAppConfirm();
                }

                if (code === 0 || code === 1 || code === 2) {
                  set({ userInfo: userInfo.data });
                  setTimeout(() => {
                    EventHub.getInstance().emit(GlobalEvent.REFRESH_CREDITS);
                  }, 1000);
                  callback?.();
                  resolve();
                } else {
                  reject();
                }
              },
              "",
              {
                position,
                project_type: PROJECT_TYPE,
              },
              isShowScenses,
              {
                scenesId: SCENE_ID,
                ...otherParam,
              }
            );
          })
          .catch(reject);
      });
    },
    receiveCredits: async () => {
      try {
        const {
          publicKey,
          userInfo,
          getters: { isLogin },
        } = get();

        if (!isLogin) return;

        if (!publicKey) {
          const { code, data } = await getPointsActivityPublicKey();
          if (code === APICode.Success) {
            set({ publicKey: data });
          }
        }

        const signData = {
          deviceId: userInfo.fotorToken,
          time: new Date().getTime(),
          uid: userInfo.fotorToken,
          type: "action_figure_first_req",
        } as any;

        const sign = await encryptWithKey(
          JSON.stringify(signData),
          get().publicKey
        );

        const { code, data } = await pointsActivityCheckIn(sign);
        if (code === APICode.Success && data) {
          const { rewards } = data;
          if (rewards && rewards.length > 0 && rewards[0].type === "credits") {
            const [firstReward] = rewards;
            const { freeQuantity } = firstReward;
            toast(
              i18n.t("received_credits", {
                credits: freeQuantity || 2,
              })
            );
            EventHub.getInstance().emit(GlobalEvent.REFRESH_CREDITS);
          }
        }
      } catch (err) {
        console.error(err);
      }
    },
    refreshFreeCount: async () => {
      get().fetchSingleRemainNums();
    },
    fetchSingleRemainNums: async () => {
      try {
        const { code, data } = await getRemainNums("aiActionFigure");
        if (code === APICode.Success) {
          set({ remainNums: data.remainNums });
        }
      } catch (err) {
        console.error(err);
      }
    }
  })
);

export default useUserStore;
